import groupedData from './us-cities-processed.json'

export interface USCity {
  name: string
  lat: string
  lng: string
}

// Pre-processed US cities grouped by state code (admin1)
// Source: cities.json npm package (CC BY 4.0, https://github.com/lutangar/cities.json)
const citiesByState = groupedData as Record<string, { n: string; la: string; lo: string }[]>

// US states + DC for the dropdown
export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

export function getCitiesForState(stateCode: string): USCity[] {
  const raw = citiesByState[stateCode] || []
  return raw.map((c) => ({ name: c.n, lat: c.la, lng: c.lo }))
}

export function findCity(stateCode: string, cityName: string): USCity | undefined {
  const cities = getCitiesForState(stateCode)
  return cities.find((c) => c.name.toLowerCase() === cityName.toLowerCase())
}

// Find a city by name across ALL states (used when the user types the city
// before picking a state). Returns the first match + its state code so the
// state dropdown can be auto-filled.
export function findCityAcrossStates(cityName: string): { city: USCity; state: string } | undefined {
  if (!cityName) return undefined
  const lower = cityName.toLowerCase()
  for (const state of Object.keys(citiesByState)) {
    const found = citiesByState[state].find((c) => c.n.toLowerCase() === lower)
    if (found) return { city: { name: found.n, lat: found.la, lng: found.lo }, state }
  }
  return undefined
}

// Flat search across all states for the city autocomplete datalist (city-first UX).
export function searchAllCities(query: string, limit = 50): USCity[] {
  const lower = query.toLowerCase()
  const out: USCity[] = []
  for (const state of Object.keys(citiesByState)) {
    for (const c of citiesByState[state]) {
      if (!lower || c.n.toLowerCase().includes(lower)) {
        out.push({ name: c.n, lat: c.la, lng: c.lo })
        if (out.length >= limit) return out
      }
    }
  }
  return out
}